import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Popover, Typography, Alert } from '@mui/material';
import { useState } from 'react';
import { useGenerateMagicLinkMutation, useGetMagicLinksQuery } from 'api/services/dataEndpoints';
import dayjs from 'dayjs';
import AuthLinksList from '../AuthLinksList/AuthLinksList';

interface ICreateAuthLinkProps {
  email?: string;
}

const CreateAuthLink: React.FC<ICreateAuthLinkProps> = ({ email }) => {
  const [copied, setCopied] = useState(false);
  const [newLink, setNewLink] = useState('');
  const [expirationTime, setExpirationTime] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [generateMagicLink, { isLoading: isGenerating }] = useGenerateMagicLinkMutation();
  const { data: links = [], isLoading: isLoadingLinks, refetch } = useGetMagicLinksQuery(email!, {
    skip: !email,
  });

  const handleGenerateClick = async () => {
    if (!email) return;

    try {
      const response = await generateMagicLink({ email }).unwrap();
      setNewLink(response.link);
      setExpirationTime(response.expires_at);
      refetch();
    } catch (err) {
      console.error('Failed to generate link:', err);
      setNewLink('');
    }
  };

  const getLinkLifetimeText = (expirationTime: string | null) => {
    if (!expirationTime) return '';
    const formatted = dayjs(expirationTime).format('MMM D, YYYY HH:mm');
    return `New link expires: ${formatted} (${dayjs(expirationTime).fromNow()})`;
  };

  const handleCopyClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!newLink) return;

    setAnchorEl(event.currentTarget);
    try {
      await navigator.clipboard.writeText(newLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ mt: '20px' }}>
        <Button
          sx={{ p: '10px' }}
          variant="outlined"
          onClick={handleGenerateClick}
          disabled={isGenerating || !email}
        >
          {isGenerating ? 'Generating...' : 'Generate New Link'}
        </Button>

      {newLink && (
        <Typography sx={{ pt: 1, pb: 2, fontWeight: 200 }} variant="body2">
          {getLinkLifetimeText(expirationTime)}
        </Typography>
      )}

      {email && (
        <AuthLinksList
          links={links}
          email={email}
          isLoading={isLoadingLinks}
          refetchList={refetch}
        />
      )}

      {!email && (
        <Alert severity="info">
          Please select a user to manage login links.
        </Alert>
      )}
    </Box>
  );
};

export default CreateAuthLink;
